import { getAuth } from 'firebase/auth'
import { initializeApp } from 'firebase/app'
import { getStorage } from 'firebase/storage'
import { getAnalytics, logEvent } from 'firebase/analytics'
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore'
import { connectFunctionsEmulator, getFunctions, httpsCallable } from 'firebase/functions'
import { initRemoteConfig } from './remote-config'

// Add SDKs for Firebase products that you want to use
// https://firebase.google.com/docs/web/setup#available-libraries

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
  authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.VUE_APP_FIREBASE_APP_ID,
  measurementId: process.env.VUE_APP_FIREBASE_MEASUREMENT_ID,
}

// Initialize Firebase
const app = initializeApp(firebaseConfig)

// Get Firebase default services
const auth = getAuth(app)
const db = getFirestore(app)
const storage = getStorage(app)
const analytics = getAnalytics(app)

const functions = (region = 'europe-west1') => getFunctions(app, region)

/**
 * Whether callables should be routed straight to a local Functions emulator
 * on 127.0.0.1:5001. This only happens in local development, and only when
 * the SDK-wide emulator connection below is not already doing it.
 * Set VUE_APP_FIREBASE_FUNCTIONS_REMOTE=true to use the hosted functions instead.
 *
 * @returns {boolean}
 */
const shouldUseLocalEmulator = () => process.env.NODE_ENV === 'development'
  && process.env.VUE_APP_FIREBASE_FUNCTIONS_EMULATOR !== 'true'
  && process.env.VUE_APP_FIREBASE_FUNCTIONS_REMOTE !== 'true'

/**
 * Calls a callable Cloud Function.
 *
 * Local development talks to the Functions emulator directly (using the
 * signed-in user from the default app, so the rest of the callables can stay
 * on sandbox). Every other environment uses the regular callable SDK.
 *
 * @param {string} name
 * @param {Object} data
 * @param {string} [region]
 * @returns {Promise<*>}
 */
const callFunction = async (name, data, region = 'europe-west1') => {
  if (!shouldUseLocalEmulator()) {
    const result = await httpsCallable(functions(region), name)(data)

    return result.data
  }

  const user = auth.currentUser

  if (!user) {
    throw new Error('Not signed in')
  }

  const token = await user.getIdToken()
  const projectId = firebaseConfig.projectId
  const response = await fetch(
    `http://127.0.0.1:5001/${projectId}/${region}/${name}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ data }),
    },
  )

  const body = await response.json().catch(() => ({}))

  if (!response.ok || body.error) {
    const error = new Error(body.error?.message || 'Emulator function failed')
    error.code = body.error?.status || response.status
    throw error
  }

  return body.result
}

// Get the custom storages
const clientThemesRootStorage = getStorage(app, process.env.VUE_APP_FIREBASE_CLIENT_THEMES_STORAGE_BUCKET)

// Get the custom databases
const themesDb = getFirestore(app, process.env.VUE_APP_FIRESTORE_THEMES_DB_ID)

// Initialize Remote Config
initRemoteConfig(app)

// > For development purpose.
// I know, 'true' seems nasty, but it has good intentions.
if (process.env.VUE_APP_FIREBASE_FUNCTIONS_EMULATOR === 'true') {
  // Connect every region the app uses so callables hit the emulator, not hosted sandbox.
  connectFunctionsEmulator(functions('europe-west1'), 'localhost', 5001)
  connectFunctionsEmulator(functions('europe-west3'), 'localhost', 5001)
}

if (process.env.VUE_APP_FIREBASE_FIRESTORE_EMULATOR === 'true') {
  connectFirestoreEmulator(db, 'localhost', 3001)
}
// < For development purpose.

export {
  db,
  auth,
  storage,
  themesDb,
  logEvent,
  analytics,
  functions,
  callFunction,
  clientThemesRootStorage,
}
